import type { SerializedViewState } from "../../../UniversalCRMTable/types";

/**
 * The live filter state as FilterContext keeps it: `advancedFilters` is an
 * ARRAY of filter groups there, whereas a saved view serializes it as an
 * object map. Anything downstream of the entity page (saved views, bulk
 * actions, export) speaks the serialized shape.
 */
export interface CurrentFiltersInput {
  advancedFilters?: unknown;
}

/**
 * Coerce the live filter state into the canonical `SerializedViewState` shape.
 *
 * The ONE rule: `advancedFilters` must end up an OBJECT. An array (the
 * FilterContext representation), `null`, `undefined`, or any non-object is
 * replaced with `{}` — downstream code indexes it by key, and an array would
 * read as "no advanced filters" only by accident while breaking spread merges.
 *
 * Returns the input untouched when it is null/undefined so the caller can keep
 * distinguishing "no filter state yet" from "empty filter state".
 */
export function normalizeCurrentFilters(
  currentFilters: CurrentFiltersInput,
): SerializedViewState;
export function normalizeCurrentFilters(
  currentFilters: CurrentFiltersInput | null | undefined,
): SerializedViewState | null | undefined;
export function normalizeCurrentFilters(
  currentFilters: CurrentFiltersInput | null | undefined,
): SerializedViewState | null | undefined {
  // Overloaded, not generic: `FilterState.currentFilters` is a NON-nullable
  // SerializedViewState, so a non-null argument must yield a non-null result —
  // the inline version this was extracted from did, by narrowing. A type
  // parameter instead leaks a TInput arm, and a single nullable signature
  // widens every caller to `| null | undefined`. Both broke the build.
  if (!currentFilters) return currentFilters;

  const { advancedFilters } = currentFilters;
  if (!advancedFilters || typeof advancedFilters !== "object" || Array.isArray(advancedFilters)) {
    return { ...currentFilters, advancedFilters: {} } as unknown as SerializedViewState;
  }

  return currentFilters as unknown as SerializedViewState;
}
